/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 9/21/2022
 * for support.userx.co
 */

import React, {useCallback, useContext, useEffect, useState} from 'react';
import Badge from 'react-bootstrap/Badge';
import {useNavigate, useParams} from 'react-router-dom';
import {UserContext} from '../_contexts/UserContext';
import {renderAlert} from '../_utilities/formattingUtils';
import {recordTicketUpdate, TICKET_STATUS} from '../_utilities/ticketUpdateService';
import useTicket from '../_hooks/useTicket';
import useDefaultsManager from '../_hooks/useDefaultsManager';
import ActivityFeed from './_ui/ActivityFeed';
import Attachments from './_ui/Attachments';
import TicketTasks from './tickets/TicketTasks';
import {TicketTitleEditor, TicketDescriptionEditor} from './tickets/TicketEditors';
import TicketSidebar from './tickets/TicketSidebar';

const Ticket = () => {
    const {ticketId} = useParams();
    // Remount drafts when navigating between tickets.
    return <TicketDetails key={ticketId} ticketId={ticketId}/>;
};

function TicketDetails({ticketId}) {
    const navigate = useNavigate();
    const {userContext} = useContext(UserContext);
    const contact = userContext?.contact;
    const groupId = String(contact?.group_id || '');
    const types = useDefaultsManager('type-defaults');
    const priorities = useDefaultsManager('priority-defaults');
    const statuses = useDefaultsManager('status-defaults');
    const [editors, setEditors] = useState({});
    const paused = Object.values(editors).some(Boolean);
    const {ticket, agentList, ticketCost, error, saving, update, refreshCost, setError} = useTicket(ticketId, paused);
    const {fetchDefaults: fetchTypes} = types;
    const {fetchDefaults: fetchPriorities} = priorities;
    const {fetchDefaults: fetchStatuses} = statuses;

    useEffect(() => {
        fetchTypes();
        fetchPriorities();
        fetchStatuses();
    }, [fetchTypes, fetchPriorities, fetchStatuses]);

    const setEditor = useCallback((name, open) => {
        setEditors(previous => previous[name] === open ? previous : {...previous, [name]: open});
    }, []);
    const setTitleEditing = useCallback(open => setEditor('title', open), [setEditor]);
    const setDescriptionEditing = useCallback(open => setEditor('description', open), [setEditor]);
    const setEstimateEditing = useCallback(open => setEditor('estimate', open), [setEditor]);
    const setActivityEditing = useCallback(open => setEditor('activity', open), [setEditor]);

    const save = async (field, value) => {
        const updated = await update(field, value);
        if (!updated) return false;
        // Secondary effects report separately so a successful save is never offered for retry.
        recordTicketUpdate({
            ticket: updated, field, value, contact,
            types: types.defaults, priorities: priorities.defaults,
            statuses: statuses.defaults, agentList
        }).catch(err => setError(err.message));
        if (field === 'status_id' && [TICKET_STATUS.COMPLETED, TICKET_STATUS.CANCELLED].includes(String(value))) {
            navigate(-1);
        }
        return true;
    };
    const findDefault = (manager, id) => manager.defaults.find(item => String(item.id) === id);
    const type = ticket ? findDefault(types, ticket.type_id) : null;
    const priority = ticket ? findDefault(priorities, ticket.priority_id) : null;
    const status = ticket ? findDefault(statuses, ticket.status_id) : null;
    const assignee = ticket ? agentList.find(agent => String(agent.contact_id) === ticket.assignee_id) : null;

    return <>
        {error && <div className="py-2 px-3 m-3" role="alert">{renderAlert(error, 'danger')}</div>}
        {ticket && contact ? <div id="ticket-wrapper" className="flex-grow-1">
            <div id="ticket-body" className="py-2 px-3 m-3 d-flex align-items-stretch">
                <h2 className="d-flex align-items-start">
                    {priority?.name && status?.name &&
                        <Badge bg={priority.color || 'secondary'} style={{marginTop: '8px', fontSize: '12px'}}
                            title={priority.name}>{status.name}</Badge>}
                    <TicketTitleEditor ticket={ticket} saving={saving} onSave={save} onEditingChange={setTitleEditing}/>
                </h2>
                <TicketDescriptionEditor ticket={ticket} type={type} saving={saving}
                    onSave={save} onEditingChange={setDescriptionEditing}/>
                <hr className="pb-1"/>
                <div className="row">
                    <TicketTasks ticket={ticket} classes="col-xs-12 col-sm-5"/>
                    <Attachments targetType="ticket" target={ticket} classes="col-xs-12 col-sm-5"/>
                </div>
                <hr className="pb-1"/>
                <div className="flex-grow-1">
                    <ActivityFeed ticket={ticket} agentList={agentList}
                        openHandler={setActivityEditing} changeHandler={refreshCost}/>
                </div>
            </div>
            <TicketSidebar ticket={ticket} groupId={groupId} ticketCost={ticketCost} agentList={agentList}
                types={types} priorities={priorities} statuses={statuses} status={status} assignee={assignee}
                saving={saving} onSave={save} onEstimateEditingChange={setEstimateEditing}
                onControlEditingChange={setEditor} onError={setError}/>
        </div> : !error && <div className="py-2 px-3 m-3 w-75">
            <Badge bg="secondary" className="my-2" style={{fontSize: '12px'}}>Loading Ticket data</Badge>
        </div>}
    </>;
}

export default Ticket;